import classNames from 'classnames';
import Button from 'components/base/Button';
import { TOKEN_CONSTANTS } from 'constant';
import { setLocalStorage } from 'helpers/localstorage-utils';
import React, { ReactNode, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useStartQuizMutation } from 'service/quizzesQuery/quizzesQuery';
import { Permission } from './quizPermissionRequest.type';
import DenyIcon from 'assets/img/permission/deny.svg';
import GrantIcon from 'assets/img/permission/grant.svg';

const titleMapping: Record<Permission, string> = {
  deny: 'Permission Request Before Quiz Start',
  granted: 'Ready to Start the Exam'
};

const descriptionMapping: Record<Permission, ReactNode> = {
  deny: (
    <>
      <p>
        Before we begin the quiz, we kindly request your permission to enable
        the required features for a smooth and fair quiz experience. This will
        help us ensure the integrity of the quiz process and track your progress
        effectively.
      </p>
      <p className="text-body-quaternary">
        Please click "Allow" to grant the necessary permissions. Your privacy is
        important to us, and the permissions will only be used for the purpose
        of this quiz.
      </p>
    </>
  ),
  granted: (
    <p>
      Thank you for granting the necessary permissions. To begin the exam,
      please click the "Start Quiz" button below. Stay focused and complete the
      exam within the allotted time.
    </p>
  )
};

const quoteMapping: Record<Permission, string> = {
  deny: 'Thank you for your cooperation!',
  granted: 'Best of luck!'
};

const IconMapping: Record<Permission, ReactNode> = {
  deny: <img src={DenyIcon} height="60%" />,
  granted: <img src={GrantIcon} height="60%" />
};

function QuizPermissionRequest() {
  const [permissionStatus, setPermissionStatus] = useState<Permission>('deny');
  const [errorMessage, setErrorMessage] = useState('');

  const { id } = useParams();
  const navigate = useNavigate();

  const [startExam, { isLoading }] = useStartQuizMutation();

  const startScreenRecording = async () => {
    try {
      await navigator.mediaDevices.getDisplayMedia({
        video: true // Only ask for video (screen)
      });

      console.log('Screen recording started!');
      setPermissionStatus('granted');
      // stream.getTracks().forEach(track => track.stop());
    } catch (error) {
      console.error('Error accessing screen: ', error);
      alert(
        'Could not start screen recording. Please ensure you granted permission.'
      );
    }
  };

  const handleStartExam = () => {
    startExam(id)
      .unwrap()
      .then(response => {
        if (typeof response.data === 'object') {
          const { token } = response.data;
          setLocalStorage(TOKEN_CONSTANTS.QUIZ, token);
          navigate('/assessment/practice/quiz');
        }
      })
      .catch(error => {
        setErrorMessage(
          error?.data?.error ||
            'An error occurred. Please close the modal and try again.'
        );
      });
  };

  const actionMapping: Record<Permission, ReactNode> = {
    granted: (
      <Button
        variant="primary"
        className="mb-2"
        onClick={handleStartExam}
        loading={isLoading}
        loadingPosition="start"
      >
        Start Quiz
      </Button>
    ),
    deny: (
      <Button variant="primary" className="mb-2" onClick={startScreenRecording}>
        Allow
      </Button>
    )
  };

  return (
    <Container fluid className="px-0">
      <Row className="vh-100 m-0">
        <Col lg={6}>
          <section className="d-flex align-items-center h-100">
            <div className="p-4">
              <p className="fs-4 mb-2">{titleMapping[permissionStatus]}</p>
              {descriptionMapping[permissionStatus]}
              {actionMapping[permissionStatus]}
              <small
                className={classNames('d-block', {
                  'text-danger': errorMessage
                })}
              >
                {errorMessage ? errorMessage : quoteMapping[permissionStatus]}
              </small>
            </div>
          </section>
        </Col>
        <Col lg={6}>
          <div className="d-flex align-items-center h-100 justify-content-center">
            {IconMapping[permissionStatus]}
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default QuizPermissionRequest;
